import React, { useState, useEffect } from "react";
import {
  Tooltip,
  Popover,
  Paper,
  useMediaQuery,
  useTheme,
  Fade,
  Box,
  Typography,
  Chip,
  Divider,
  Skeleton,
} from "@mui/material";
import { FiberManualRecord } from "@mui/icons-material";
import moment from "moment";
import axios from "../services/Api.js";

// Hook for fetching employee basic data
const useGetEmployeeBasicData = ({ userId, skip }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || skip) {
      setData(null);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await axios.get("/api/employee/getEmployeeBasicData", {
          params: { user_id: userId },
        });
        setData(res.data);
      } catch (err) {
        setError(err);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId, skip]);

  return { data, isLoading, error };
};

// Hook for fetching student details
const useGetStudentDetails = ({ auid, skip }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auid || skip) {
      setData(null);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `/api/student/getStudentDetailsBasedOnAuidAndStrudentId?auid=${auid}`
        );
        setData(response.data);
      } catch (err) {
        setError(err);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [auid, skip]);

  return { data, isLoading, error };
};

// Skeleton component for loading state
const UserCardSkeleton = ({ type = "employee" }) => {
  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box flex={1}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
        </Box>
        <Skeleton
          variant="rectangular"
          width={60}
          height={24}
          sx={{ borderRadius: 1 }}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
    </Box>
  );
};

// Employee details component
const EmployeeDetails = ({ employeeInfo, label }) => {
  if (!employeeInfo) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No information available.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight="600">
            {employeeInfo.employee_name || employeeInfo.name || ""}
          </Typography>
          {label && (
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          )}
        </Box>
        {employeeInfo.role_name && (
          <Chip label={employeeInfo.role_name} color="primary" size="small" />
        )}
      </Box>

      {/* Contact & Department Section */}
      <Divider sx={{ my: 2 }} />
      <Box display="flex" flexDirection="column" gap={1.5}>
        {employeeInfo.email && (
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Email
            </Typography>
            <Typography
              variant="body2"
              textAlign="right"
              noWrap
              sx={{ maxWidth: "60%" }}
            >
              {employeeInfo.email}
            </Typography>
          </Box>
        )}
        {(employeeInfo.department_short_name ||
          employeeInfo.dept_name_short) && (
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Department
            </Typography>
            <Typography variant="body2" textAlign="right">
              {employeeInfo.department_short_name ||
                employeeInfo.dept_name_short}
            </Typography>
          </Box>
        )}
        {employeeInfo.designation_name && (
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Designation
            </Typography>
            <Typography variant="body2" textAlign="right">
              {employeeInfo.designation_name}
            </Typography>
          </Box>
        )}
        {employeeInfo.school_short_name && (
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              School
            </Typography>
            <Typography variant="body2" textAlign="right">
              {employeeInfo.school_short_name}
            </Typography>
          </Box>
        )}
        {employeeInfo.empcode && (
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Emp Code
            </Typography>
            <Typography variant="body2" textAlign="right">
              {employeeInfo.empcode}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Dates Section */}
      {employeeInfo.date_of_joining && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Joined
            </Typography>
            <Typography variant="body2" textAlign="right">
              {moment(employeeInfo.date_of_joining).format("DD-MM-YYYY")}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

// Student details component
const StudentDetails = ({ studentDetail, label }) => {
  if (!studentDetail) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No information available.
      </Typography>
    );
  }

  const email = studentDetail?.acharya_email || studentDetail?.email;
  const hasAcademicInfo =
    studentDetail?.program_name ||
    studentDetail?.nationalityName ||
    studentDetail?.academic_batch ||
    studentDetail?.school_name_short ||
    studentDetail?.program_type_name ||
    studentDetail?.semOrYear ||
    studentDetail?.current_sem ||
    studentDetail?.current_year ||
    studentDetail?.usn ||
    studentDetail?.program_specialization_short_name ||
    studentDetail?.fee_admission_category_short_name ||
    studentDetail?.proctorName;

  return (
    <Box>
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight="600">
            {studentDetail?.student_name || studentDetail?.firstname || ""}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            {studentDetail?.auid && (
              <>
                <Typography variant="caption" color="text.secondary">
                  {studentDetail.auid}
                </Typography>
                {studentDetail?.usn && (
                  <>
                    <FiberManualRecord
                      sx={{ fontSize: 8, color: "text.secondary" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {studentDetail.usn}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
        <Chip label="Student" color="success" size="small" />
      </Box>

      {/* Email Section */}
      {email && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Email
            </Typography>
            <Typography
              variant="body2"
              textAlign="right"
              noWrap
              sx={{ maxWidth: "60%" }}
            >
              {email}
            </Typography>
          </Box>
        </>
      )}

      {/* Academic Information Section */}
      {hasAcademicInfo && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" flexDirection="column" gap={1.5}>
            {studentDetail?.program_name && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Program
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.program_name}
                </Typography>
              </Box>
            )}
            {studentDetail?.nationalityName && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Nationality
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.nationalityName}
                </Typography>
              </Box>
            )}
            {studentDetail?.academic_batch && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Academic Batch
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.academic_batch}
                </Typography>
              </Box>
            )}
            {studentDetail?.school_name_short && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  School
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.school_name_short}
                </Typography>
              </Box>
            )}
            {studentDetail?.program_type_name && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Program Type
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.program_type_name}
                </Typography>
              </Box>
            )}
            {studentDetail?.semOrYear && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Sem/Year
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.semOrYear}
                </Typography>
              </Box>
            )}
            {studentDetail?.current_sem && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Current Sem
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.current_sem}
                </Typography>
              </Box>
            )}
            {studentDetail?.current_year && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Current Year
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.current_year}
                </Typography>
              </Box>
            )}
            {studentDetail?.usn && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  USN
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.usn}
                </Typography>
              </Box>
            )}
            {studentDetail?.program_specialization_short_name && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Specialization
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.program_specialization_short_name}
                </Typography>
              </Box>
            )}
            {studentDetail?.fee_admission_category_short_name && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Admission Category
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.fee_admission_category_short_name}
                </Typography>
              </Box>
            )}
            {studentDetail?.proctorName && (
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography
                  variant="body2"
                  fontWeight="500"
                  color="text.secondary"
                >
                  Proctor
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {studentDetail.proctorName}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Date of Admission Section */}
      {studentDetail?.date_of_admission && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              Date of Admission
            </Typography>
            <Typography variant="body2" textAlign="right">
              {moment(studentDetail.date_of_admission).format("DD-MM-YYYY")}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

// Main UserCard component
const UserCard = ({
  userId,
  label,
  children,
  className = "",
  isStudent = false,
  /** When true, renders plain text only — no hover/popover or getEmployeeBasicData / student preview. */
  disableHoverDetails = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const detailsOpen = isMobile ? !!popoverAnchor : tooltipOpen;

  const { data: employeeData, isLoading: isLoadingEmployee } =
    useGetEmployeeBasicData({
      userId,
      skip:
        !userId ||
        isStudent ||
        !detailsOpen ||
        disableHoverDetails,
    });

  const { data: studentData, isLoading: isLoadingStudent } =
    useGetStudentDetails({
      auid: userId || "",
      skip:
        !userId ||
        !isStudent ||
        !detailsOpen ||
        disableHoverDetails,
    });

  // If no userId provided, just render the label/children as-is
  if (!userId) {
    return <span className={className}>{children ?? label ?? ""}</span>;
  }

  if (disableHoverDetails) {
    return (
      <span className={className}>{children ?? label ?? ""}</span>
    );
  }

  const employeeInfo = employeeData?.data;
  const studentDetail = studentData?.data?.[0];

  const handlePopoverOpen = (event) => {
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  const triggerContent = (
    <span
      className={className}
      style={{
        borderBottom: "1px dashed currentColor",
        cursor: "pointer",
        display: "inline-block",
      }}
      onMouseEnter={() => !isMobile && setTooltipOpen(true)}
      onMouseLeave={() => !isMobile && setTooltipOpen(false)}
      onClick={isMobile ? handlePopoverOpen : undefined}
    >
      {children ?? label}
    </span>
  );

  const detailsContent = (
    <Paper
      elevation={8}
      sx={{
        width: 320,
        p: 2,
        maxWidth: "90vw",
      }}
    >
      {isStudent ? (
        isLoadingStudent ? (
          <UserCardSkeleton type="student" />
        ) : (
          <StudentDetails studentDetail={studentDetail} label={label} />
        )
      ) : isLoadingEmployee ? (
        <UserCardSkeleton />
      ) : (
        <EmployeeDetails employeeInfo={employeeInfo} label={label} />
      )}
    </Paper>
  );

  if (isMobile) {
    return (
      <>
        {triggerContent}
        <Popover
          open={!!popoverAnchor}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          TransitionComponent={Fade}
        >
          {detailsContent}
        </Popover>
      </>
    );
  }

  return (
    <Tooltip
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      title={detailsContent}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            p: 0,
            maxWidth: 320,
            boxShadow: 3,
          },
        },
        arrow: {
          sx: {
            color: "background.paper",
          },
        },
      }}
    >
      {triggerContent}
    </Tooltip>
  );
};

export default UserCard;
